import { cx } from '@emotion/css'
import React, { useCallback, useEffect, useState } from 'react'
import Measure, { type ContentRect } from 'react-measure'

import { ExtendedPaletteColorName } from '../../shared/MobiusColors'
import type { SemanticColorToken } from '../themes'
import { CssVars, NeutralColorToken, type ThemeColorToken } from '../themes'
import { Accordion } from './Accordion'
import { InternalElevate } from './internal/InternalElevate'
import { TextButton } from './TextButton'
import { TextColorOverride } from './TextColorOverride'
import { getColorValue, useTheme } from './ThemeProvider'
import { Type } from './Type'

type CallToActionProps =
  | {}
  | {
      /**
       * The text to use as a call to action.
       */
      callToAction: string
      /**
       * A callback to be called when the user clicks on the call to action.
       */
      onCallToActionClick: (
        event: React.MouseEvent<HTMLButtonElement, MouseEvent>
      ) => void
    }

type ComponentProps = {
  /**
   * The primary text to show in the `StatusBanner`.
   * Typed as ReactNode to support rendering Rich Text.
   *
   * TODO: Narrow this type to RichText-only once https://robinhood.atlassian.net/browse/DS-1835 is done
   */
  title: React.ReactNode
  /**
   * If provided, this color will be used for the background of the `InfoBanner`. Defaults to
   * Mineral for light themes and elevated BG1 for dark themes.
   */
  backgroundColor?: SemanticColorToken | ThemeColorToken
  /**
   * If provided, this color will be used for the text and CTA. Defaults to FG1.
   */
  textColor?: SemanticColorToken | ThemeColorToken
  /**
   * If provided, this color will be used for the rule line. Defaults to FG1.
   */
  ruleLineColor?: SemanticColorToken | ThemeColorToken
  /**
   * Width of the inner content container. The status banner will fill the entire width of what it is placed in,
   * but the content inside of it should match the rest of the page's content.
   * For Web App, this should be the "app width."
   */
  contentWidth: number
} & CallToActionProps

type Props = {
  /**
   * Listen for the inner height of the Status Banner's contents.
   * Useful for computing the necessary page offset if the banner renders with position: fixed.
   */
  onHeightChange?: (height: number) => void
  /**
   * Listen for when the Status Banner begins to appear on-screen.
   * This happens 50+ ms after initial render (dependent on the underlying animation + render cycles) and may be useful
   * for timing the page offset position when rendering with position: fixed.
   *
   * NOTE: onAppear() will be called at animation start, *not* at animation end.
   * This allows components which depend on StatusBanner's appearance/height to adjust simultaneously and run
   * their own animations in parallel.
   */
  onAppear?: () => void
  /**
   * If enabled, will skip the appearance animation. Useful if the banner is already visible but needs to re-render
   * due to the React component tree.
   */
  skipAnimate?: boolean
} & ComponentProps

const StatusBannerComponent = React.forwardRef(function _StatusBannerComponent(
  {
    backgroundColor: backgroundColorProp,
    contentWidth,
    internalElevateClassName,
    title,
    textColor = NeutralColorToken.Fg1,
    ruleLineColor = NeutralColorToken.Fg1,
    ...optionalProps
  }: ComponentProps & {
    internalElevateClassName: string
  },
  ref: React.Ref<HTMLDivElement>
) {
  const theme = useTheme()

  const defaultBackgroundColor = theme.isDark
    ? NeutralColorToken.Bg1
    : ExtendedPaletteColorName.Mineral
  const backgroundColor = backgroundColorProp ?? defaultBackgroundColor

  const backgroundColorVariable = getColorValue(backgroundColor, theme)
  const ruleLineColorVariable = getColorValue(ruleLineColor, theme)

  return (
    <TextColorOverride color={textColor}>
      {({ rootClassName: textColorOverrideRootClassName }) => (
        <div
          ref={ref}
          className={cx(
            internalElevateClassName,
            textColorOverrideRootClassName
          )}
          css={{
            alignItems: 'center',
            backgroundColor: backgroundColorVariable,
            borderBottom: `1px solid ${ruleLineColorVariable}`,
            display: 'flex',
            justifyContent: 'center',
            marginBottom: `calc(2 * ${CssVars.size.gutterWidthSm})`,
            paddingBottom: CssVars.size.gutterWidthSm,
            paddingTop: CssVars.size.gutterWidthSm,
            width: '100%',
          }}
          role="status"
        >
          <div
            css={{
              alignItems: 'center',
              display: 'flex',
              flex: 1,
              marginLeft: CssVars.size.gutterWidth,
              marginRight: CssVars.size.gutterWidth,
              maxWidth: contentWidth,
            }}
          >
            <Type.Text size="M">
              {title}
              {'callToAction' in optionalProps && (
                <>
                  {' '}
                  <TextButton
                    color={textColor}
                    isLegacyStyle
                    label={optionalProps.callToAction}
                    onClick={optionalProps.onCallToActionClick}
                    type="button"
                    variant="inline"
                  />
                </>
              )}
            </Type.Text>
          </div>
        </div>
      )}
    </TextColorOverride>
  )
})

/**
 * A `StatusBanner` is a global, top-of-page banner displayed across all pages of the app.
 * There should never be more than one of these displayed at a time, with ideally a single location in the entire app
 * that consolidates display logic & rendering.
 * For Web App, this is directly driven by the In-App Comms backend. See more: http://go/iac
 */
export function StatusBanner({
  onAppear,
  onHeightChange,
  skipAnimate,
  ...props
}: Props) {
  const [isOpen, setIsOpen] = useState(!!skipAnimate)
  useEffect(() => {
    // Toggle "visible" state once after render to ensure entry animation.
    // There is no exit animation for status banners.
    setIsOpen(true)
  }, [])

  const handleAccordionStateChange = useCallback(
    (isAccordionOpen: boolean) => {
      if (onAppear && isAccordionOpen) {
        onAppear()
      }
    },
    [onAppear]
  )
  const handleResize = useCallback(
    (contentRect: ContentRect) => {
      // Offset height is content + padding + border, but does not include margin
      const offsetHeight = contentRect.offset?.height || 0
      // Combine top + bottom margin to get the complete height of the status bar
      const margin = contentRect.margin
        ? contentRect.margin.top + contentRect.margin.bottom
        : 0
      if (onHeightChange) {
        onHeightChange(offsetHeight + margin)
      }
    },
    [onHeightChange]
  )

  return (
    <Accordion
      isOpen={isOpen}
      onStateChange={handleAccordionStateChange}
      overrideRootStyles={{
        // Enable the Status Banner to "slide down from the top" rather than it's default "appear from top to bottom"
        // This allows the bottom rule line (border) to appear at the start, rather than suddenly appearing at the
        // animation end.
        display: 'flex',
        flexFlow: 'column nowrap',
        justifyContent: 'flex-end',
      }}
    >
      <InternalElevate>
        {({ rootClassName }) => (
          <Measure margin offset onResize={handleResize}>
            {({ measureRef }: { measureRef: React.Ref<HTMLDivElement> }) => (
              <StatusBannerComponent
                ref={measureRef}
                internalElevateClassName={rootClassName}
                {...props}
              />
            )}
          </Measure>
        )}
      </InternalElevate>
    </Accordion>
  )
}
